import React from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import PhoneInput, { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-phone-number-input/style.css';

const DEFAULT_COUNTRY_CODE = 'DE';

export function isValidE164Phone(value) {
  const normalized = String(value || '').replace(/\s+/g, '');
  if (!/^\+[1-9]\d{7,14}$/.test(normalized)) return false;
  return isValidPhoneNumber(normalized);
}

function getCountryFromPhone(value, fallback) {
  try {
    const parsed = parsePhoneNumber(String(value || ''));
    return parsed?.country || fallback;
  } catch {
    return fallback;
  }
}

function DialCodeCountrySelect({ value, onChange, options, disabled, className, id, iconComponent: Icon }) {
  const filteredOptions = (options || []).filter((opt) => !!opt.value);
  const resolvedValue = value || (filteredOptions[0]?.value ?? '');
  const displayValue = resolvedValue || '';

  let DialLabel = '';
  try {
    DialLabel = displayValue ? `+${getCountryCallingCode(displayValue)}` : '';
  } catch {
    DialLabel = displayValue ? `+${displayValue}` : '';
  }

  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setQuery('');
      searchRef.current?.focus?.();
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const renderLabel = (opt) => {
    const country = opt?.value;
    let dial = country ? `+${country}` : '';
    try {
      if (country) dial = `+${getCountryCallingCode(country)}`;
    } catch {
      //
    }
    return opt?.label ? `${dial} ${opt.label}` : dial;
  };

  const dialOnlyText = DialLabel || (displayValue ? `+${displayValue}` : '');

  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions =
    normalizedQuery.length === 0
      ? filteredOptions
      : filteredOptions.filter((opt) => {
          const name = String(opt.label || '').toLowerCase();
          const country = opt.value;
          let dial = '';
          try {
            dial = country ? `+${getCountryCallingCode(country)}` : '';
          } catch {
            dial = country ? `+${country}` : '';
          }
          const dialDigits = dial.replace(/[^\d]/g, '');
          const qDigits = normalizedQuery.replace(/[^\d]/g, '');
          const matchesName = name.includes(normalizedQuery);
          const matchesDial = dial.includes(normalizedQuery) || (qDigits && dialDigits.includes(qDigits));
          return matchesName || matchesDial;
        });

  return (
    <div ref={rootRef} className='flex items-center gap-3 shrink-0 relative'>
      <button
        id={id}
        type='button'
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`${className} !w-[108px] !max-w-[108px] relative flex items-center gap-2 px-1.5 pr-6`}
        aria-label={DialLabel ? `Country calling code ${DialLabel}` : 'Country calling code'}
        aria-haspopup='listbox'
        aria-expanded={open}
      >
        <span className='flex items-center gap-2 min-w-0'>
          {Icon && displayValue ? (
            <span className='w-5 h-3.5 rounded-sm overflow-hidden bg-muted flex items-center justify-center shrink-0'>
              {React.createElement(Icon, { country: displayValue, className: 'w-full h-full object-cover' })}
            </span>
          ) : null}
          <span className='whitespace-nowrap'>{dialOnlyText}</span>
        </span>
        <svg
          className='pointer-events-none absolute right-1.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground'
          viewBox='0 0 20 20'
          fill='currentColor'
          aria-hidden='true'
        >
          <path
            fillRule='evenodd'
            d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.24 4.5a.75.75 0 0 1-1.08 0l-4.24-4.5a.75.75 0 0 1 .02-1.06Z'
            clipRule='evenodd'
          />
        </svg>
      </button>

      {open ? (
        <div
          role='listbox'
          aria-label='Select country'
        className='absolute z-50 mt-2 top-full left-0 w-[min(280px,calc(100vw-48px))] max-h-80 overflow-auto rounded-md border bg-white shadow-lg'
        >
          <div className='sticky top-0 bg-white border-b p-2'>
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search country or code…'
              className='w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            />
          </div>
          {visibleOptions.length === 0 ? <div className='px-3 py-2 text-sm text-muted-foreground'>No matches</div> : null}
          {visibleOptions.map((opt) => {
            const isSelected = opt.value === displayValue;
            return (
              <button
                key={opt.value}
                type='button'
                role='option'
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value || undefined);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-muted/50 ${isSelected ? 'bg-primary/10' : ''}`}
              >
                {Icon ? (
                  <span className='w-6 h-4 rounded-sm overflow-hidden bg-muted flex items-center justify-center shrink-0'>
                    {React.createElement(Icon, { country: opt.value, className: 'w-full h-full object-cover' })}
                  </span>
                ) : null}
                <span className='truncate'>{renderLabel(opt)}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

DialCodeCountrySelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  iconComponent: PropTypes.elementType,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
};

DialCodeCountrySelect.defaultProps = {
  value: undefined,
  options: [],
  iconComponent: undefined,
  disabled: false,
  className: '',
  id: undefined,
};

function PhoneNumberInput({ id, value, onChange, disabled, defaultCountryCode, allowedCountries, inputClassName, selectClassName, placeholder }) {
  const fallbackCountry = (defaultCountryCode || DEFAULT_COUNTRY_CODE).toUpperCase();
  const [country, setCountry] = React.useState(() => getCountryFromPhone(value, fallbackCountry));

  React.useEffect(() => {
    const fromValue = getCountryFromPhone(value, null);
    if (fromValue) {
      setCountry(fromValue);
      return;
    }
    if (!String(value || '').trim() && fallbackCountry) {
      setCountry(fallbackCountry);
    }
  }, [value, fallbackCountry]);

  const countrySelectProps = React.useMemo(
    () => ({
      id: id ? `${id}-country` : undefined,
      className: selectClassName,
    }),
    [id, selectClassName],
  );

  return (
    <div className='flex items-center w-full'>
      <PhoneInput
        id={id}
        className='flex items-center gap-3 w-full'
        value={value || ''}
        onChange={(next) => onChange(next || '')}
        disabled={disabled}
        international={false}
        defaultCountry={country || fallbackCountry}
        onCountryChange={(nextCountry) => {
          if (nextCountry) setCountry(nextCountry);
        }}
        countries={Array.isArray(allowedCountries) && allowedCountries.length > 0 ? allowedCountries : undefined}
        countrySelectComponent={DialCodeCountrySelect}
        countrySelectProps={countrySelectProps}
        numberInputProps={{
          className: `${inputClassName} flex-1 min-w-0`,
          placeholder,
          inputMode: 'numeric',
          autoComplete: 'tel-national',
        }}
      />
    </div>
  );
}

PhoneNumberInput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  defaultCountryCode: PropTypes.string,
  allowedCountries: PropTypes.arrayOf(PropTypes.string),
  inputClassName: PropTypes.string,
  selectClassName: PropTypes.string,
  placeholder: PropTypes.string,
};

PhoneNumberInput.defaultProps = {
  id: undefined,
  value: '',
  disabled: false,
  defaultCountryCode: DEFAULT_COUNTRY_CODE,
  allowedCountries: undefined,
  inputClassName: '',
  selectClassName: '',
  placeholder: 'Phone number',
};

export default PhoneNumberInput;
